import React from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Button } from '../../../../components/ui';
import { useFeatureTheme } from '../../../../theme/hooks/useFeatureTheme';
import { ANALYTICS_COPY } from '../../constants/analyticsCopy';
import {
  CUSTOM_DATE_PRESET,
  DATE_PRESET_IDS,
  DEFAULT_DATE_PRESET,
  NUMBER_DATE_PRESET_IDS,
} from '../../utils/datePresets';
import { kolkataToday, addDays } from '../../utils/previousPeriod';

const PRESET_LABELS = {
  today: ANALYTICS_COPY.datePresetToday,
  yesterday: ANALYTICS_COPY.datePresetYesterday,
  last_7_days: ANALYTICS_COPY.datePresetLast7,
  last_30_days: ANALYTICS_COPY.datePresetLast30,
  last_90_days: ANALYTICS_COPY.datePresetLast90,
  this_month: ANALYTICS_COPY.datePresetThisMonth,
  this_week: ANALYTICS_COPY.datePresetThisWeek,
  all_time: ANALYTICS_COPY.datePresetAllTime,
};

const NUMBER_PRESET_HINTS = {
  all_time: ANALYTICS_COPY.datePresetHintAllTime,
  today: ANALYTICS_COPY.datePresetHintToday,
  this_week: ANALYTICS_COPY.datePresetHintThisWeek,
};

function selectedPreset(dateRange) {
  if (DATE_PRESET_IDS.includes(dateRange?.preset)) return dateRange.preset;
  if (dateRange?.preset === CUSTOM_DATE_PRESET || dateRange?.start || dateRange?.end) {
    return CUSTOM_DATE_PRESET;
  }
  return DEFAULT_DATE_PRESET;
}

export default function ReportDateRange({
  dateRange = {},
  onChange,
  disabled = false,
  presets = DATE_PRESET_IDS,
  rangeHint = '',
}) {
  const { semantic, typography } = useFeatureTheme();
  const isNumberRange = presets === NUMBER_DATE_PRESET_IDS
    || (presets.length === NUMBER_DATE_PRESET_IDS.length
      && NUMBER_DATE_PRESET_IDS.every((id) => presets.includes(id)));
  const preset = presets.includes(dateRange?.preset)
    ? dateRange.preset
    : (isNumberRange ? 'all_time' : selectedPreset(dateRange));
  const customLabel = [dateRange.start, dateRange.end].filter(Boolean).join(' – ');

  const handlePresetSelect = (id) => {
    if (id === 'today') {
      const today = kolkataToday();
      onChange({ preset: 'today', start: today, end: today });
      return;
    }
    if (id === 'yesterday') {
      const yDay = addDays(kolkataToday(), -1);
      onChange({ preset: 'yesterday', start: yDay, end: yDay });
      return;
    }
    onChange({ preset: id });
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography variant="subtitle2" fontWeight={700}>
          {ANALYTICS_COPY.builderStep2DateLabel}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {ANALYTICS_COPY.valueInclusionOptional}
        </Typography>
      </Stack>
      {isNumberRange ? (
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={preset}
          disabled={disabled}
          onChange={(_, next) => next && handlePresetSelect(next)}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              py: 1,
            },
          }}
        >
          {presets.map((id) => (
            <ToggleButton key={id} value={id}>
              {PRESET_LABELS[id]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {presets.map((id) => (
            <Button
              key={id}
              size="sm"
              variant={preset === id ? 'primary' : 'outline'}
              disabled={disabled}
              onClick={() => handlePresetSelect(id)}
            >
              {PRESET_LABELS[id]}
            </Button>
          ))}
        </Box>
      )}
      {preset === CUSTOM_DATE_PRESET ? (
        <Typography
          sx={{
            fontSize: typography.fontSizeMd,
            color: semantic.text.secondary,
          }}
        >
          {ANALYTICS_COPY.datePresetCustomLocked.replace('{range}', customLabel)}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {rangeHint || NUMBER_PRESET_HINTS[preset] || ANALYTICS_COPY.datePresetHint}
        </Typography>
      )}
    </Stack>
  );
}
